import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuCheckboxItem,
  DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuItem,
} from '@/components/ui/dropdown-menu';
import { MetricCard } from '@/components/MetricCard';
import { PieChart, ChartContainer } from '@/components/charts';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from '@/components/ui/command';
import { useDebounce } from '@/hooks/useDebounce';
import { useAccountHealthOverview, useAllAccounts } from '../hooks';
import { getAccountHealthOverview } from '../api';
import type { AccountHealth } from '../types';
import type { ColumnDef } from '../utils/csvExport';
import { exportToCSV, copyToClipboard } from '../utils/csvExport';
import {
  RefreshCw, Building2, CheckCircle, AlertCircle, XCircle,
  Search, ChevronRight, X, Settings2, Download, Copy,
  ArrowUp, ArrowDown, ArrowUpDown, Check, ChevronsUpDown,
} from 'lucide-react';
import { cn } from '@/lib/utils';

type HealthFilter = 'all' | 'healthy' | 'warning' | 'critical';
type HealthStatus = Exclude<HealthFilter, 'all'> | 'unknown';

const PAGE_SIZE = 25;
const VISIBILITY_STORAGE_KEY = 'atek-customer-health-column-visibility';
const FILTER_STORAGE_KEY = 'atek-customer-health-column-filters';

function getHealthStatus(score: number | null | undefined): HealthStatus {
  if (score == null) return 'unknown';
  if (score >= 80) return 'healthy';
  if (score >= 50) return 'warning';
  return 'critical';
}

function getHealthBadgeClass(status: HealthStatus) {
  switch (status) {
    case 'healthy': return 'bg-green-100 text-green-800 border-green-200';
    case 'warning': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    case 'critical': return 'bg-red-100 text-red-800 border-red-200';
    case 'unknown': return 'bg-gray-100 text-gray-700 border-gray-200';
    default: return 'bg-gray-100 text-gray-800 border-gray-200';
  }
}

function formatScore(score: number | null | undefined): string {
  return score != null ? String(score) : '--';
}

// ---------------------------------------------------------------------------
// Column Visibility Helpers
// ---------------------------------------------------------------------------

function getDefaultVisibility<T>(columns: ColumnDef<T>[]): Record<string, boolean> {
  const defaults: Record<string, boolean> = {};
  for (const col of columns) {
    defaults[col.key] = col.defaultVisible !== false;
  }
  return defaults;
}

function loadColumnVisibility<T>(columns: ColumnDef<T>[]): Record<string, boolean> {
  try {
    const stored = localStorage.getItem(VISIBILITY_STORAGE_KEY);
    if (stored) return { ...getDefaultVisibility(columns), ...JSON.parse(stored) };
  } catch { /* ignore */ }
  return getDefaultVisibility(columns);
}

function saveColumnVisibility(visibility: Record<string, boolean>) {
  localStorage.setItem(VISIBILITY_STORAGE_KEY, JSON.stringify(visibility));
}

// ---------------------------------------------------------------------------
// SortableHeader
// ---------------------------------------------------------------------------

function SortableHeader({
  label,
  sortKey,
  currentSortKey,
  sortDirection,
  onSort,
}: {
  label: string;
  sortKey: string;
  currentSortKey?: string;
  sortDirection?: 1 | -1;
  onSort: (key: string) => void;
}) {
  const isActive = currentSortKey === sortKey;
  return (
    <Button
      variant="ghost"
      size="sm"
      className="-ml-3 h-8 hover:bg-transparent"
      onClick={() => onSort(sortKey)}
    >
      {label}
      {isActive ? (
        sortDirection === 1 ? <ArrowUp className="ml-1 h-3 w-3" /> : <ArrowDown className="ml-1 h-3 w-3" />
      ) : (
        <ArrowUpDown className="ml-1 h-3 w-3 opacity-50" />
      )}
    </Button>
  );
}

// ---------------------------------------------------------------------------
// SearchableSelectFilter (combobox with type-ahead search)
// ---------------------------------------------------------------------------

function SearchableSelectFilter({
  colKey,
  options,
  value,
  onChange,
}: {
  colKey: string;
  options: { label: string; value: string }[];
  value: string;
  onChange: (key: string, value: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const selected: string[] = useMemo(() => {
    try { return value ? JSON.parse(value) : []; }
    catch { return []; }
  }, [value]);

  const filtered = useMemo(() => {
    if (!search) return options;
    const q = search.toLowerCase();
    return options.filter((o) => o.label.toLowerCase().includes(q));
  }, [options, search]);

  const toggle = (opt: string) => {
    const next = selected.includes(opt)
      ? selected.filter((o) => o !== opt)
      : [...selected, opt];
    onChange(colKey, next.length ? JSON.stringify(next) : '');
  };

  const selectedLabel = useMemo(() => {
    if (selected.length === 0) return null;
    if (selected.length === 1) {
      const match = options.find((o) => o.value === selected[0]);
      return match?.label || '1 selected';
    }
    return `${selected.length} selected`;
  }, [selected, options]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="h-7 w-full text-xs justify-between font-normal">
          <span className="truncate">{selectedLabel || '\u2014'}</span>
          {selected.length > 0 ? (
            <X className="h-3 w-3 shrink-0 opacity-50 hover:opacity-100"
              onClick={(e) => { e.stopPropagation(); onChange(colKey, ''); }} />
          ) : (
            <ChevronsUpDown className="h-3 w-3 shrink-0 opacity-50" />
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-56 p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput placeholder="Search..." value={search} onValueChange={setSearch} className="h-8 text-xs" />
          <CommandList>
            <CommandEmpty className="py-2 text-center text-xs text-muted-foreground">No results</CommandEmpty>
            <CommandGroup className="max-h-48 overflow-y-auto">
              {filtered.map((opt) => (
                <CommandItem key={opt.value} value={opt.value} onSelect={() => toggle(opt.value)} className="text-xs">
                  <div className={cn('mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-primary',
                    selected.includes(opt.value) ? 'bg-primary text-primary-foreground' : 'opacity-50')}>
                    {selected.includes(opt.value) && <Check className="h-3 w-3" />}
                  </div>
                  {opt.label}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

// ---------------------------------------------------------------------------
// Column Filter Renderer
// ---------------------------------------------------------------------------

function renderColumnFilter<T>(
  col: ColumnDef<T>,
  columnFilters: Record<string, string>,
  setColumnFilter: (key: string, value: string) => void,
  searchableSelectOptions?: Record<string, { label: string; value: string }[]>,
) {
  if (col.noFilter) return <TableHead key={`filter-${col.key}`} />;

  // Searchable-select filter (combobox with search)
  if (col.filterType === 'searchable-select') {
    const options = searchableSelectOptions?.[col.key] || [];
    return (
      <TableHead key={`filter-${col.key}`} className="p-1">
        <SearchableSelectFilter
          colKey={col.key}
          options={options}
          value={columnFilters[col.key] || ''}
          onChange={setColumnFilter}
        />
      </TableHead>
    );
  }

  // Multi-select filter
  if (col.filterType === 'select' && col.filterOptions) {
    const selected: string[] = (() => {
      try { return columnFilters[col.key] ? JSON.parse(columnFilters[col.key]) : []; }
      catch { return []; }
    })();

    const toggle = (opt: string) => {
      const next = selected.includes(opt)
        ? selected.filter((o) => o !== opt)
        : [...selected, opt];
      setColumnFilter(col.key, next.length ? JSON.stringify(next) : '');
    };

    return (
      <TableHead key={`filter-${col.key}`} className="p-1">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" className="h-7 w-full text-xs justify-start font-normal">
              {selected.length ? `${selected.length} selected` : '\u2014'}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-44">
            {col.filterOptions.map((opt) => (
              <DropdownMenuCheckboxItem
                key={opt.value}
                checked={selected.includes(opt.value)}
                onCheckedChange={() => toggle(opt.value)}
              >
                {opt.label}
              </DropdownMenuCheckboxItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </TableHead>
    );
  }

  // Date range filter
  if (col.filterType === 'date-range') {
    const raw = columnFilters[col.key] || '';
    const [from = '', to = ''] = raw.split('>>');
    return (
      <TableHead key={`filter-${col.key}`} className="p-1">
        <div className="flex flex-col gap-0.5">
          <Input
            type="date"
            className="h-6 text-xs px-1"
            value={from}
            onChange={(e) => setColumnFilter(col.key, `${e.target.value}>>${to}`)}
            title="From"
          />
          <Input
            type="date"
            className="h-6 text-xs px-1"
            value={to}
            onChange={(e) => setColumnFilter(col.key, `${from}>>${e.target.value}`)}
            title="To"
          />
        </div>
      </TableHead>
    );
  }

  // Number range filter
  if (col.filterType === 'number-range') {
    const raw = columnFilters[col.key] || '';
    const [min = '', max = ''] = raw.split('>>');
    return (
      <TableHead key={`filter-${col.key}`} className="p-1">
        <div className="flex flex-col gap-0.5">
          <Input
            type="number"
            className="h-6 text-xs px-1"
            value={min}
            onChange={(e) => setColumnFilter(col.key, `${e.target.value}>>${max}`)}
            placeholder="Min"
          />
          <Input
            type="number"
            className="h-6 text-xs px-1"
            value={max}
            onChange={(e) => setColumnFilter(col.key, `${min}>>${e.target.value}`)}
            placeholder="Max"
          />
        </div>
      </TableHead>
    );
  }

  // Text filter (default)
  return (
    <TableHead key={`filter-${col.key}`} className="p-1">
      <Input
        className="h-7 text-xs"
        value={columnFilters[col.key] || ''}
        onChange={(e) => setColumnFilter(col.key, e.target.value)}
        placeholder="..."
      />
    </TableHead>
  );
}

// ===========================================================================
// Main Component
// ===========================================================================

export default function CustomerHealthPage() {
  const { t } = useTranslation('intelligence');

  // ---------------------------------------------------------------------------
  // Column Definitions
  // ---------------------------------------------------------------------------

  const COLUMNS = useMemo<ColumnDef<AccountHealth>[]>(() => [
    {
      key: 'name',
      label: t('customers.table.customer'),
      sortKey: 'name',
      filterType: 'searchable-select',
      getValue: (a) => a.name,
      render: (a) => <span className="font-medium text-gray-900">{a.name}</span>,
    },
    {
      key: 'healthScore',
      label: t('customers.table.healthScore'),
      sortKey: 'health_score',
      filterType: 'number-range',
      getValue: (a) => a.healthScore,
      render: (a) => {
        const status = getHealthStatus(a.healthScore);
        return (
          <Badge className={cn('font-semibold', getHealthBadgeClass(status))}>
            {formatScore(a.healthScore)}
          </Badge>
        );
      },
      align: 'center',
    },
    {
      key: 'subscription',
      label: t('customers.table.subscription'),
      sortKey: 'subscription',
      filterType: 'number-range',
      getValue: (a) => a.subscriptionHealthScore,
      align: 'center',
    },
    {
      key: 'payment',
      label: t('customers.table.payment'),
      sortKey: 'payment',
      filterType: 'number-range',
      getValue: (a) => a.paymentHealthScore,
      align: 'center',
    },
    {
      key: 'engagement',
      label: t('customers.table.engagement'),
      sortKey: 'engagement',
      filterType: 'number-range',
      getValue: (a) => a.engagementHealthScore,
      align: 'center',
    },
    {
      key: 'support',
      label: t('customers.table.support'),
      sortKey: 'support',
      filterType: 'number-range',
      getValue: (a) => a.supportHealthScore,
      align: 'center',
    },
    {
      key: 'actions',
      label: '',
      noFilter: true,
      getValue: () => '',
      render: (a) => (
        <Link
          to={`/intelligence/customers/${a.id}`}
          className="text-blue-600 hover:text-blue-800"
          onClick={(e) => e.stopPropagation()}
        >
          <ChevronRight className="h-5 w-5 inline" />
        </Link>
      ),
      align: 'right',
    },
  ], [t]);

  // ---------------------------------------------------------------------------
  // Server-side state
  // ---------------------------------------------------------------------------

  const [page, setPage] = useState(0);
  const [statusFilter, setStatusFilter] = useState<HealthFilter>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearch = useDebounce(searchQuery, 300);

  // ---------------------------------------------------------------------------
  // Column filters (client-side refinement, persisted)
  // ---------------------------------------------------------------------------

  const [columnFilters, setColumnFilters] = useState<Record<string, string>>(() => {
    try { return JSON.parse(localStorage.getItem(FILTER_STORAGE_KEY) || '{}'); }
    catch { return {}; }
  });

  useEffect(() => {
    localStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(columnFilters));
  }, [columnFilters]);

  const setColumnFilter = useCallback((key: string, value: string) => {
    setColumnFilters((prev) => ({ ...prev, [key]: value }));
  }, []);

  // Promote text column filter values to server-side search
  const columnSearchText = useMemo(() => {
    return Object.entries(columnFilters)
      .filter(([, value]) => {
        if (!value) return false;
        if (value.includes('>>')) return false;
        if (value.startsWith('[')) return false;
        return true;
      })
      .map(([, value]) => value)
      .join(' ');
  }, [columnFilters]);

  const debouncedColumnSearch = useDebounce(columnSearchText, 300);

  // Combined server-side search
  const combinedSearch = [debouncedSearch, debouncedColumnSearch].filter(Boolean).join(' ') || '';

  // Reset page when column search changes
  useEffect(() => {
    if (debouncedColumnSearch) setPage(0);
  }, [debouncedColumnSearch]);

  // ---------------------------------------------------------------------------
  // Column visibility (persisted)
  // ---------------------------------------------------------------------------

  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>(
    () => loadColumnVisibility(COLUMNS)
  );

  const toggleColumn = useCallback((key: string) => {
    setColumnVisibility((prev) => {
      const updated = { ...prev, [key]: !prev[key] };
      saveColumnVisibility(updated);
      return updated;
    });
  }, []);

  const isVisible = useCallback((key: string) => columnVisibility[key] !== false, [columnVisibility]);

  const visibleColumns = useMemo(
    () => COLUMNS.filter((col) => isVisible(col.key)),
    [COLUMNS, isVisible]
  );

  // ---------------------------------------------------------------------------
  // Client-side sort (within page)
  // ---------------------------------------------------------------------------

  const [sortKey, setSortKey] = useState<string | undefined>('health_score');
  const [sortDirection, setSortDirection] = useState<1 | -1>(1);

  const handleSort = useCallback((key: string) => {
    if (key === sortKey) {
      setSortDirection((d) => (d === 1 ? -1 : 1));
    } else {
      setSortKey(key);
      setSortDirection(1);
    }
    setPage(0);
  }, [sortKey]);

  // ---------------------------------------------------------------------------
  // Data fetching
  // ---------------------------------------------------------------------------

  const { data, isLoading, error, refetch, isFetching } = useAccountHealthOverview({
    page,
    pageSize: PAGE_SIZE,
    risk: statusFilter,
    search: combinedSearch,
    sortKey,
    sortDirection,
  });

  const accountsQuery = useAllAccounts();
  const accountOptions = useMemo(() => {
    const accts = accountsQuery.data || [];
    const seen = new Set<string>();
    return accts.reduce<{ label: string; value: string }[]>((acc, a) => {
      if (!seen.has(a.name)) { seen.add(a.name); acc.push({ label: a.name, value: a.name }); }
      return acc;
    }, []);
  }, [accountsQuery.data]);

  const searchableSelectOptions = useMemo(
    () => ({ name: accountOptions }),
    [accountOptions],
  );

  const accounts = data?.accounts || [];
  const total = data?.total || 0;
  const serverSummary = data?.summary;
  const totalPages = Math.ceil(total / PAGE_SIZE) || 1;

  // ---------------------------------------------------------------------------
  // Client-side filtering (non-text, within page)
  // ---------------------------------------------------------------------------

  const filteredItems = useMemo(() => {
    const activeFilters = Object.entries(columnFilters).filter(([, v]) => v);
    if (activeFilters.length === 0) return accounts;

    return accounts.filter((item) =>
      activeFilters.every(([colKey, filterValue]) => {
        const col = COLUMNS.find((c) => c.key === colKey);
        if (!col || col.noFilter) return true;

        const rawValue = col.getValue(item);

        // Text filters are promoted to server-side — skip client-side
        if (col.filterType === 'text' || (!col.filterType && !filterValue.includes('>>') && !filterValue.startsWith('['))) {
          return true;
        }

        // Date range
        if (col.filterType === 'date-range') {
          const [from, to] = filterValue.split('>>');
          const dateStr = String(rawValue || '');
          if (from && dateStr < from) return false;
          if (to && dateStr > to + 'T23:59:59') return false;
          return true;
        }

        // Multi-select
        if (col.filterType === 'select' || col.filterType === 'searchable-select') {
          try {
            const selected = JSON.parse(filterValue) as string[];
            return selected.length === 0 || selected.includes(String(rawValue || ''));
          } catch { return true; }
        }

        // Number range
        if (col.filterType === 'number-range') {
          const [minStr, maxStr] = filterValue.split('>>');
          const num = Number(rawValue);
          if (rawValue == null || isNaN(num)) {
            return !minStr && !maxStr;
          }
          if (minStr && num < Number(minStr)) return false;
          if (maxStr && num > Number(maxStr)) return false;
          return true;
        }

        return true;
      })
    );
  }, [accounts, columnFilters, COLUMNS]);

  // ---------------------------------------------------------------------------
  // Client-side sort
  // ---------------------------------------------------------------------------

  const sortedItems = useMemo(() => {
    if (!sortKey) return filteredItems;
    const col = COLUMNS.find((c) => c.sortKey === sortKey);
    if (!col) return filteredItems;

    return [...filteredItems].sort((a, b) => {
      const aVal = col.getValue(a);
      const bVal = col.getValue(b);
      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return 1;
      if (bVal == null) return -1;
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return (aVal - bVal) * sortDirection;
      }
      return String(aVal).localeCompare(String(bVal)) * sortDirection;
    });
  }, [filteredItems, sortKey, sortDirection, COLUMNS]);

  // ---------------------------------------------------------------------------
  // Slide panel
  // ---------------------------------------------------------------------------

  const [selectedAccount, setSelectedAccount] = useState<AccountHealth | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!selectedAccount) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setSelectedAccount(null);
    };
    document.addEventListener('keydown', handleKeyDown);
    panelRef.current?.focus();

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [selectedAccount]);

  // ---------------------------------------------------------------------------
  // Export handlers
  // ---------------------------------------------------------------------------

  const [isExportingAll, setIsExportingAll] = useState(false);

  const handleExportAll = useCallback(async () => {
    try {
      setIsExportingAll(true);
      const allItems: AccountHealth[] = [];
      const batchSize = 100;
      let pageNum = 0;
      let totalCount = 0;

      do {
        const batch = await getAccountHealthOverview({
          page: pageNum,
          pageSize: batchSize,
          risk: statusFilter !== 'all' ? statusFilter : undefined,
          search: combinedSearch || undefined,
          sortKey,
          sortDirection,
        });
        allItems.push(...(batch.accounts || []));
        totalCount = batch.total ?? 0;
        pageNum++;
      } while (allItems.length < totalCount);

      exportToCSV(allItems, COLUMNS, 'customer-health-all');
      toast.success(`Exported ${allItems.length} rows`);
    } catch {
      toast.error('Failed to export all data');
    } finally {
      setIsExportingAll(false);
    }
  }, [statusFilter, combinedSearch, COLUMNS, sortKey, sortDirection]);

  // ---------------------------------------------------------------------------
  // Handler helpers
  // ---------------------------------------------------------------------------

  const handlePageChange = useCallback((newPage: number) => {
    setPage(newPage);
    setSelectedAccount(null);
  }, []);

  const handleStatusFilterChange = useCallback((status: HealthFilter) => {
    setStatusFilter(status);
    setPage(0);
    setSelectedAccount(null);
  }, []);

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setPage(0);
    setSelectedAccount(null);
  }, []);

  // ---------------------------------------------------------------------------
  // Summary data
  // ---------------------------------------------------------------------------

  const summary = useMemo(() => {
    if (serverSummary) return serverSummary;
    return { total: 0, healthy: 0, warning: 0, critical: 0 };
  }, [serverSummary]);

  const distributionChartData = useMemo(() => [
    { name: t('customers.status.healthy'), value: summary.healthy },
    { name: t('customers.status.warning'), value: summary.warning },
    { name: t('customers.status.critical'), value: summary.critical },
  ], [summary, t]);

  // ---------------------------------------------------------------------------
  // Error state
  // ---------------------------------------------------------------------------

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center justify-between">
          <span className="text-red-700">{t('common:error')}: {error.message}</span>
          <Button variant="outline" onClick={() => refetch()}>
            {t('common:retry')}
          </Button>
        </div>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div className="p-6 space-y-6 bg-gray-50 min-h-full">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('customers.title')}</h1>
          <p className="text-sm text-gray-500 mt-1">{t('customers.subtitle')}</p>
        </div>
      </div>

      {/* Summary Cards + Distribution Chart */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <MetricCard
          title={t('customers.cards.total')}
          value={summary.total}
          icon={<Building2 className="h-5 w-5" />}
          color="blue"
          loading={isLoading}
        />
        <MetricCard
          title={t('customers.cards.healthy')}
          value={summary.healthy}
          icon={<CheckCircle className="h-5 w-5" />}
          color="green"
          loading={isLoading}
        />
        <MetricCard
          title={t('customers.cards.warning')}
          value={summary.warning}
          icon={<AlertCircle className="h-5 w-5" />}
          color="orange"
          loading={isLoading}
        />
        <MetricCard
          title={t('customers.cards.critical')}
          value={summary.critical}
          icon={<XCircle className="h-5 w-5" />}
          color="red"
          loading={isLoading}
        />
        <ChartContainer loading={isLoading} height={120}>
          <PieChart
            data={distributionChartData}
            height={100}
            colors={['#27ae60', '#f39c12', '#e74c3c']}
            innerRadius={30}
          />
        </ChartContainer>
      </div>

      {/* Server-side Filter Bar */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex gap-2">
          {(['all', 'healthy', 'warning', 'critical'] as HealthFilter[]).map((status) => (
            <Button
              key={status}
              variant={statusFilter === status ? 'default' : 'outline'}
              size="sm"
              onClick={() => handleStatusFilterChange(status)}
            >
              {t(`customers.filters.${status}`)}
            </Button>
          ))}
        </div>
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            placeholder={t('customers.search')}
            value={searchQuery}
            onChange={handleSearchChange}
            className="pl-10"
          />
        </div>
      </div>

      {/* Account Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>{t('customers.table.customer')}s</CardTitle>
              <CardDescription>
                {filteredItems.length < accounts.length
                  ? `Showing ${filteredItems.length} of ${accounts.length} on this page`
                  : `Showing ${accounts.length} of ${total}`}
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  copyToClipboard(sortedItems, COLUMNS);
                  toast.success('Copied to clipboard');
                }}
              >
                <Copy className="h-4 w-4 mr-1" />
                Copy
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" disabled={isExportingAll}>
                    <Download className="h-4 w-4 mr-1" />
                    {isExportingAll ? 'Exporting...' : 'CSV'}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuLabel>Export</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => {
                    exportToCSV(sortedItems, COLUMNS, 'customer-health');
                    toast.success(`Exported ${sortedItems.length} rows`);
                  }}>
                    Export Page ({sortedItems.length} rows)
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleExportAll}>
                    Export All ({total})
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <Button
                variant="outline"
                size="sm"
                onClick={() => refetch()}
                disabled={isFetching}
              >
                <RefreshCw className={cn('h-4 w-4 mr-1', isFetching && 'animate-spin')} />
                Refresh
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm">
                    <Settings2 className="h-4 w-4 mr-1" />
                    Columns
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuLabel>Toggle columns</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {COLUMNS.filter((c) => !c.noFilter).map((col) => (
                    <DropdownMenuCheckboxItem
                      key={col.key}
                      checked={isVisible(col.key)}
                      onCheckedChange={() => toggleColumn(col.key)}
                    >
                      {col.label}
                    </DropdownMenuCheckboxItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              {/* ROW 1: Filter inputs */}
              <TableRow className="bg-muted/30 hover:bg-muted/30">
                {visibleColumns.map((col) => renderColumnFilter(col, columnFilters, setColumnFilter, searchableSelectOptions))}
              </TableRow>

              {/* ROW 2: Column labels + sort */}
              <TableRow>
                {visibleColumns.map((col) => (
                  <TableHead
                    key={col.key}
                    className={cn(
                      'text-xs font-medium text-gray-500 uppercase tracking-wider',
                      col.align === 'center' && 'text-center',
                      col.align === 'right' && 'text-right',
                    )}
                  >
                    {col.sortKey ? (
                      <SortableHeader
                        label={col.label}
                        sortKey={col.sortKey}
                        currentSortKey={sortKey}
                        sortDirection={sortDirection}
                        onSort={handleSort}
                      />
                    ) : (
                      col.label
                    )}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>

            <TableBody>
              {/* Loading */}
              {isLoading && Array.from({ length: 5 }).map((_, i) => (
                <TableRow key={i}>
                  {visibleColumns.map((col) => (
                    <TableCell key={col.key}>
                      <Skeleton className="h-4 w-full" />
                    </TableCell>
                  ))}
                </TableRow>
              ))}

              {/* Empty */}
              {!isLoading && sortedItems.length === 0 && (
                <TableRow>
                  <TableCell colSpan={visibleColumns.length} className="py-12 text-center text-muted-foreground">
                    {t('customers.noResults')}
                  </TableCell>
                </TableRow>
              )}

              {/* Data rows */}
              {!isLoading && sortedItems.map((account) => (
                <TableRow
                  key={account.id}
                  className="cursor-pointer hover:bg-muted/50"
                  tabIndex={0}
                  role="button"
                  onClick={() => setSelectedAccount(account)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setSelectedAccount(account);
                    }
                  }}
                >
                  {visibleColumns.map((col) => (
                    <TableCell
                      key={col.key}
                      className={cn(
                        'text-sm',
                        col.align === 'center' && 'text-center',
                        col.align === 'right' && 'text-right tabular-nums',
                        col.className,
                      )}
                    >
                      {col.render
                        ? col.render(account)
                        : (String(col.getValue(account) ?? '') || '\u2014')}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {/* Pagination */}
          {total > 0 && totalPages > 1 && (
            <div className="border-t px-4 py-3 flex items-center justify-between">
              <p className="text-sm text-muted-foreground">
                {t('customers.pagination.showing', {
                  from: page * PAGE_SIZE + 1,
                  to: Math.min((page + 1) * PAGE_SIZE, total),
                  total,
                })}
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(page - 1)}
                  disabled={page === 0 || isFetching}
                >
                  {t('common:previous')}
                </Button>
                <span className="text-sm text-gray-600">
                  {t('customers.pagination.pageOf', { page: page + 1, totalPages })}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(page + 1)}
                  disabled={page >= totalPages - 1 || isFetching}
                >
                  {t('common:next')}
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Detail Slide Panel */}
      {selectedAccount && (
        <>
          <div
            className="fixed inset-0 bg-black/30 z-40"
            onClick={() => setSelectedAccount(null)}
          />
          <div
            ref={panelRef}
            role="dialog"
            aria-modal="true"
            aria-label={selectedAccount.name}
            tabIndex={-1}
            className="fixed right-0 top-0 h-full w-full sm:w-[400px] bg-white shadow-xl z-50 overflow-y-auto outline-none"
          >
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold">
                {selectedAccount.name}
              </h2>
              <Button variant="ghost" size="icon" onClick={() => setSelectedAccount(null)}>
                <X className="h-5 w-5" />
              </Button>
            </div>
            <div className="p-6 space-y-6">
              {/* Health Score */}
              <div className="text-center py-6">
                <div className={cn(
                  'text-5xl font-bold',
                  getHealthStatus(selectedAccount.healthScore) === 'healthy' && 'text-green-600',
                  getHealthStatus(selectedAccount.healthScore) === 'warning' && 'text-yellow-600',
                  getHealthStatus(selectedAccount.healthScore) === 'critical' && 'text-red-600',
                  getHealthStatus(selectedAccount.healthScore) === 'unknown' && 'text-gray-500',
                )}>
                  {formatScore(selectedAccount.healthScore)}
                </div>
                <div className="text-sm text-gray-500 uppercase mt-2">
                  {t(`customers.status.${getHealthStatus(selectedAccount.healthScore)}`)}
                </div>
              </div>

              {/* Health Factors */}
              <div className="space-y-4">
                <h3 className="font-medium text-gray-900">{t('customers.detail.factors')}</h3>
                {[
                  { key: 'subscription', value: selectedAccount.subscriptionHealthScore, weight: 30 },
                  { key: 'payment', value: selectedAccount.paymentHealthScore, weight: 35 },
                  { key: 'engagement', value: selectedAccount.engagementHealthScore, weight: 20 },
                  { key: 'support', value: selectedAccount.supportHealthScore, weight: 15 },
                ].map((factor) => (
                  <div key={factor.key} className="space-y-1">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">
                        {t(`customers.factors.${factor.key}`)} ({factor.weight}%)
                      </span>
                      <span className="font-medium">{factor.value != null ? `${factor.value}` : '--'}</span>
                    </div>
                    <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className={cn(
                          'h-full rounded-full transition-all',
                          (factor.value || 0) >= 80 && 'bg-green-500',
                          (factor.value || 0) >= 50 && (factor.value || 0) < 80 && 'bg-yellow-500',
                          (factor.value || 0) < 50 && 'bg-red-500',
                        )}
                        style={{ width: `${factor.value || 0}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>

              {/* Actions */}
              <div className="pt-4 border-t">
                <Link
                  to={`/intelligence/customers/${selectedAccount.id}`}
                  className="block"
                >
                  <Button className="w-full">
                    {t('customers.detail.viewDetails')}
                    <ChevronRight className="h-4 w-4 ml-2" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
